import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Target, RotateCcw, Save, Trophy, Package } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ROLE_LABEL = {
  driver: 'Drivers',
  internal_staff: 'Internal Staff (Delivery)',
};

const R = (n) => 'R ' + Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * IncentiveSettings — admin can tune parcel targets & bonus per role.
 */
export default function IncentiveSettings() {
  const [rows, setRows] = useState([]);
  const [defaults, setDefaults] = useState(null);
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/incentive-settings`);
      setRows(r.data?.roles || []);
      setDefaults(r.data?.defaults || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load settings');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const setField = (role, k, v) => setRows((prev) => prev.map((r) => r.role === role ? { ...r, [k]: v } : r));

  const save = async (row) => {
    setSaving((s) => ({ ...s, [row.role]: true }));
    try {
      await axios.put(`${API}/incentive-settings/${row.role}`, {
        daily_target: Number(row.daily_target),
        weekly_target: Number(row.weekly_target),
        monthly_target: Number(row.monthly_target),
        incentive_amount: Number(row.incentive_amount),
      });
      toast.success(`${ROLE_LABEL[row.role]} settings saved`);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving((s) => ({ ...s, [row.role]: false })); }
  };

  const reset = async (role) => {
    if (!window.confirm(`Reset ${ROLE_LABEL[role]} to code defaults?`)) return;
    setSaving((s) => ({ ...s, [role]: true }));
    try {
      await axios.post(`${API}/incentive-settings/${role}/reset`);
      toast.success('Reset to defaults');
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally { setSaving((s) => ({ ...s, [role]: false })); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Trophy className="h-6 w-6 text-amber-500" /> Incentive & Parcel Targets
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Change parcel targets and the monthly bonus per role without touching the code.
              The Payslip auto-fill and Staff Dashboard banners will use whatever you set here immediately.
            </p>
          </div>
        </div>

        {defaults && (
          <Card className="bg-slate-50 border-slate-200">
            <CardContent className="p-3 text-xs text-slate-600 flex items-center gap-4 flex-wrap">
              <span className="font-semibold text-slate-700">Code defaults:</span>
              <span>Daily <b>{defaults.daily_target}</b></span>
              <span>Weekly <b>{defaults.weekly_target}</b></span>
              <span>Monthly <b>{defaults.monthly_target}</b></span>
              <span>Bonus <b>{R(defaults.incentive_amount)}</b></span>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <Card><CardContent className="text-center py-6">Loading…</CardContent></Card>
        ) : rows.map((row) => (
          <Card key={row.role} data-testid={`role-card-${row.role}`}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Package className="h-4 w-4 text-cyan-600" />
                {ROLE_LABEL[row.role] || row.role}
                {row.updated_at && (
                  <Badge variant="outline" className="ml-2 bg-emerald-50 text-emerald-700 border-emerald-300 text-[10px]">
                    Custom override
                  </Badge>
                )}
              </CardTitle>
              {row.updated_at && (
                <CardDescription className="text-xs">
                  Last saved by <b>{row.updated_by || '—'}</b> on {new Date(row.updated_at).toLocaleString('en-ZA')}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Daily target</Label>
                  <div className="flex items-center gap-1">
                    <Input
                      type="number" min="1"
                      value={row.daily_target}
                      onChange={(e) => setField(row.role, 'daily_target', e.target.value)}
                      data-testid={`daily-${row.role}`}
                    />
                    <span className="text-xs text-slate-400">pcs</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Weekly target</Label>
                  <div className="flex items-center gap-1">
                    <Input
                      type="number" min="1"
                      value={row.weekly_target}
                      onChange={(e) => setField(row.role, 'weekly_target', e.target.value)}
                      data-testid={`weekly-${row.role}`}
                    />
                    <span className="text-xs text-slate-400">pcs</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1"><Target className="h-3 w-3" /> Monthly target</Label>
                  <div className="flex items-center gap-1">
                    <Input
                      type="number" min="1"
                      value={row.monthly_target}
                      onChange={(e) => setField(row.role, 'monthly_target', e.target.value)}
                      data-testid={`monthly-${row.role}`}
                    />
                    <span className="text-xs text-slate-400">pcs</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1"><Trophy className="h-3 w-3 text-amber-500" /> Incentive bonus</Label>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-slate-400">R</span>
                    <Input
                      type="number" min="0" step="0.01"
                      value={row.incentive_amount}
                      onChange={(e) => setField(row.role, 'incentive_amount', e.target.value)}
                      data-testid={`bonus-${row.role}`}
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => reset(row.role)}
                  disabled={saving[row.role] || !row.updated_at}
                  data-testid={`reset-${row.role}`}
                >
                  <RotateCcw className="h-3 w-3 mr-1" /> Reset to defaults
                </Button>
                <Button
                  size="sm"
                  onClick={() => save(row)}
                  disabled={saving[row.role]}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid={`save-${row.role}`}
                >
                  <Save className="h-3 w-3 mr-1" /> {saving[row.role] ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </DashboardLayout>
  );
}
